import {
  TailwindColor,
  tailwindColors,
  TailwindColorValue
} from '@repo/tailwind-config/tailwind.vars'

import { LocaleKeys } from '../../../../locale/translation-types'
import { Center } from '../../models/center/center'

export enum CenterTripsStatusEnum {
  COMPLETED = 'COMPLETED',
  PENDING = 'PENDING',
  MISSING = 'MISSING'
}

/**
 * Travel-info diffusion state, decided by the backend.
 *
 * The colours used to be inferred here from `tripBookingDiffused` + `tripBookingDiffusedAt`, which
 * could not tell "a leg was never diffused" apart from "something diffused was modified" — both
 * arrived as `false`, so a brand new leg was announced as a modification.
 */
export enum CenterTripBookingDiffusionStatusEnum {
  NOT_DIFFUSABLE = 'NOT_DIFFUSABLE',
  PENDING_FIRST_DIFFUSION = 'PENDING_FIRST_DIFFUSION',
  DIFFUSED = 'DIFFUSED',
  MODIFIED_AFTER_DIFFUSION = 'MODIFIED_AFTER_DIFFUSION',
  /** OL-only state: a diffusion the OL has not opened yet. */
  NEW_DIFFUSION_TO_VIEW = 'NEW_DIFFUSION_TO_VIEW'
}

type CenterTripsStatusOption = {
  textColor: TailwindColor
  bgColor: TailwindColor
}

export function getCenterTripInfo({
  tripsStatus
}: Pick<Center, 'tripsStatus'>): CenterTripsStatusOption {
  // MISSING already means "nothing diffused yet" — the backend owns that decision now.
  if (tripsStatus === CenterTripsStatusEnum.MISSING) {
    return {
      bgColor: tailwindColors.grey.light.DEFAULT,
      textColor: tailwindColors.grey.DEFAULT
    }
  }

  const isCompleted = tripsStatus === CenterTripsStatusEnum.COMPLETED
  const color = isCompleted ? tailwindColors.green : tailwindColors.red

  return {
    bgColor: color.light.DEFAULT,
    textColor: color.DEFAULT
  }
}

type CenterTripBookingInfo = {
  textColor: TailwindColor
  bgColor: TailwindColor
  label: LocaleKeys
  /** The "(!)" icon means "re-diffuse what changed", so only a real modification earns it. */
  showModifiedIcon: boolean
  showDiffusedAt: boolean
}

/** Only the shaded palette entries — `tailwindColors.transparent` has no `light`/`DEFAULT`. */
type DiffusionStatusColor = Extract<TailwindColorValue, 'grey' | 'orange' | 'green' | 'red'>

const DIFFUSION_STATUS_DISPLAY: Record<
  CenterTripBookingDiffusionStatusEnum,
  {
    color: DiffusionStatusColor
    label: LocaleKeys
    showModifiedIcon: boolean
    showDiffusedAt: boolean
  }
> = {
  [CenterTripBookingDiffusionStatusEnum.NOT_DIFFUSABLE]: {
    color: 'grey',
    label: 'ol.center.table.travel.info.not.defined',
    showModifiedIcon: false,
    showDiffusedAt: false
  },
  [CenterTripBookingDiffusionStatusEnum.PENDING_FIRST_DIFFUSION]: {
    color: 'orange',
    label: 'ol.center.table.travel.info.first.diffusion.pending',
    showModifiedIcon: false,
    showDiffusedAt: false
  },
  [CenterTripBookingDiffusionStatusEnum.DIFFUSED]: {
    color: 'green',
    label: 'ol.center.table.travel.info.diffused',
    showModifiedIcon: false,
    showDiffusedAt: true
  },
  [CenterTripBookingDiffusionStatusEnum.MODIFIED_AFTER_DIFFUSION]: {
    color: 'red',
    label: 'ol.center.table.travel.info.not.diffused',
    showModifiedIcon: true,
    showDiffusedAt: true
  },
  [CenterTripBookingDiffusionStatusEnum.NEW_DIFFUSION_TO_VIEW]: {
    color: 'red',
    label: 'ol.center.table.travel.info.new.diffusion',
    showModifiedIcon: true,
    showDiffusedAt: true
  }
}

export function getCenterTripBookingInfo({
  tripBookingDiffusionStatus
}: Pick<Center, 'tripBookingDiffusionStatus'>): CenterTripBookingInfo {
  const display =
    DIFFUSION_STATUS_DISPLAY[
      tripBookingDiffusionStatus ?? CenterTripBookingDiffusionStatusEnum.NOT_DIFFUSABLE
    ]

  return {
    bgColor: tailwindColors[display.color].light.DEFAULT,
    textColor: tailwindColors[display.color].DEFAULT,
    label: display.label,
    showModifiedIcon: display.showModifiedIcon,
    showDiffusedAt: display.showDiffusedAt
  }
}

/**
 * LEC can push travel info to the OL when there is something to send that the OL has not seen in
 * its current form: a first diffusion, or a diffusion outdated by later edits.
 */
export function canDiffuseTripBookings(
  tripBookingDiffusionStatus: Center['tripBookingDiffusionStatus']
): boolean {
  return (
    tripBookingDiffusionStatus === CenterTripBookingDiffusionStatusEnum.PENDING_FIRST_DIFFUSION ||
    tripBookingDiffusionStatus === CenterTripBookingDiffusionStatusEnum.MODIFIED_AFTER_DIFFUSION
  )
}
